import { Map } from 'immutable'
import { RESOLVER_TYPE_NOT_FOUND } from '../../services/resolver/types'
import { parse as parseQuery } from '../../services/query-string/index'
import { SEO_ALLOWED_INDEX_QUERY_PARAMS, SITE_NAME, getPageTitle } from '../../services/page/index'
import {
  FB_PROPERTY_APP_ID,
  OG_PROPERTY_DESCRIPTION,
  OG_PROPERTY_IMAGE,
  OG_PROPERTY_IMAGE_HEIGHT,
  OG_PROPERTY_IMAGE_WIDTH,
  OG_PROPERTY_SITE_NAME,
  OG_PROPERTY_TITLE,
  OG_PROPERTY_TYPE,
  OG_PROPERTY_URL,
  TWITTER_CARD,
  TWITTER_DESCRIPTION,
  TWITTER_IMAGE,
  TWITTER_TITLE,
} from './constants'
import type {
  DocumentHeadLink,
  DocumentHeadMeta,
  DocumentHeadModel,
} from './types'

type StateMap = Map<string, unknown>

export interface DocumentHeadLocation {
  pathname: string
  search: string
  query?: Record<string, unknown>
}

interface CreateDocumentHeadModelOptions {
  contentLang: string
  interstitial: StateMap
  isWebView: boolean
  jwt?: string | null
  location: DocumentHeadLocation
  page: StateMap
  resolver: StateMap
  scrollable?: boolean
}

const NO_PAGE: StateMap = Map()

function asString (value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

/**
 * Redux `page` lags the router during a client navigation: the location changes
 * first and the destination's SEO is stored afterwards. While `page` still
 * identifies the route the user left, its title, description and canonical
 * describe a page that is no longer displayed, so the head must not use them.
 *
 * `page.path` is written as pathname + search (services/page/actions), and is
 * absent until a route stores SEO, which is not a mismatch.
 */
function isPageForLocation (page: StateMap, location: DocumentHeadLocation): boolean {
  const path = asString(page.get('path'))
  if (!path) return true
  return path === `${location.pathname}${location.search || ''}`
}

function getLink (page: StateMap, location: DocumentHeadLocation): DocumentHeadLink[] {
  const links: DocumentHeadLink[] = [
    { rel: 'icon', type: 'img/png', href: '/favicon.ico?ver=0.0.2' },
    {
      rel: 'canonical',
      href: asString(page.get('canonical')) ?? location.pathname,
    },
  ]
  const prev = asString(page.get('prev'))
  const next = asString(page.get('next'))
  if (prev) links.push({ rel: 'prev', href: prev })
  if (next) links.push({ rel: 'next', href: next })
  return links
}

function getMetaRobots (
  page: StateMap,
  location: DocumentHeadLocation,
  resolver: StateMap,
): DocumentHeadMeta[] {
  const queryKeys = Object.keys(parseQuery(location.search))
  const seoAllowedQueryParams = queryKeys.length === 0
    || queryKeys.some((key) => SEO_ALLOWED_INDEX_QUERY_PARAMS.includes(key))
  const defaultNoIndex = resolver.getIn(['data', 'type']) === RESOLVER_TYPE_NOT_FOUND
  const noFollow = Boolean(page.get('noFollow', false))
  const noIndex = !seoAllowedQueryParams || Boolean(page.get('noIndex', defaultNoIndex))

  if (!noFollow && !noIndex) return []

  const content = [
    ...(noFollow ? ['nofollow'] : []),
    ...(noIndex ? ['noindex'] : []),
  ].join(', ')
  return [{ name: 'robots', content }]
}

function metaByName (name: string, value: unknown): DocumentHeadMeta[] {
  const content = asString(value)
  return content ? [{ name, content }] : []
}

function metaByProperty (property: string, value: unknown): DocumentHeadMeta[] {
  const content = asString(value)
  return content ? [{ property, content }] : []
}

function getMeta (
  page: StateMap,
  location: DocumentHeadLocation,
  resolver: StateMap,
): DocumentHeadMeta[] {
  return [
    ...metaByName('description', page.get('description')),
    ...metaByProperty(FB_PROPERTY_APP_ID, page.get('facebookAppId')),
    ...metaByProperty(OG_PROPERTY_SITE_NAME, page.get('ogSiteName')),
    ...metaByProperty(OG_PROPERTY_TITLE, page.get('ogTitle')),
    ...metaByProperty(OG_PROPERTY_IMAGE, page.get('ogImage')),
    ...metaByProperty(OG_PROPERTY_IMAGE_WIDTH, page.get('ogImageWidth')),
    ...metaByProperty(OG_PROPERTY_IMAGE_HEIGHT, page.get('ogImageHeight')),
    ...metaByProperty(OG_PROPERTY_URL, page.get('ogUrl')),
    ...metaByProperty(OG_PROPERTY_TYPE, page.get('ogType')),
    ...metaByProperty(OG_PROPERTY_DESCRIPTION, page.get('ogDescription')),
    ...getMetaRobots(page, location, resolver),
    ...metaByName(TWITTER_CARD, page.get('twitterCard')),
    ...metaByName(TWITTER_IMAGE, page.get('twitterImage')),
    ...metaByName(TWITTER_TITLE, page.get('twitterTitle')),
    ...metaByName(TWITTER_DESCRIPTION, page.get('twitterDescription')),
  ]
}

function getHtmlClass (
  isWebView: boolean,
  interstitial: StateMap,
  scrollable: boolean,
  jwt: string | null,
): string {
  const classes = [jwt ? 'app--member' : 'app--anon']
  if (isWebView) classes.push('app--webview')
  if (scrollable === false) classes.push('app--noscroll')
  if (interstitial.get('view')) classes.push('app--interstitial')
  if (interstitial.get('leaving')) classes.push('app--interstitial-leaving')
  return classes.join(' ')
}

export function createDocumentHeadModel ({
  contentLang,
  interstitial,
  isWebView,
  jwt = null,
  location,
  page,
  resolver,
  scrollable = true,
}: CreateDocumentHeadModelOptions): DocumentHeadModel {
  const pageIsCurrent = isPageForLocation(page, location)
  const currentPage = pageIsCurrent ? page : NO_PAGE

  return {
    htmlAttributes: {
      class: getHtmlClass(isWebView, interstitial, scrollable, jwt),
      'data-embed': location.query?.embed === 'true',
      lang: contentLang,
    },
    link: getLink(currentPage, location),
    meta: getMeta(currentPage, location, resolver),
    title: pageIsCurrent ? getPageTitle(page) : SITE_NAME,
  }
}
